/* eslint-disable react/jsx-no-bind */
import React, { useRef, useState } from 'react';
import { translate } from '../../../../base/i18n/functions';
import { copyText } from '../../../../base/util/copyText.web';
import { LOCKED_LOCALLY } from '../../../../room-lock/constants';
import { NOTIFY_CLICK_MODE } from '../../../../toolbox/constants';
import Button from "../../../../base/ui/components/web/Button";
import { BUTTON_TYPES } from "../../../../base/ui/constants.any";
import { makeStyles } from "tss-react/mui";
import PasswordForm from "../../../../../11sight/features/security/components/security-dialog/PasswordForm";
import CopyButton from "../../../../base/buttons/CopyButton.web";
const useStyles = makeStyles()(theme => ({
    container: {
        width: "100%",
        marginTop: theme.spacing(2)
    },
    formButtonsContainer: {
        display: "flex",
        justifyContent: "flex-end",
        gap: theme.spacing(2)
    },
    controlButtonsContainer: {
        display: "flex",
        justifyContent: "space-between"
    },
    cancelButton: {
        color: theme.palette.iisightDefaultText
    }
}));
const DIGITS_ONLY = /^\d+$/;
const KEY = 'add-passcode';
/**
 * Component that handles the password manipulation from the invite dialog.
 *
 * @returns {React$Element<any>}
 */
function PasswordSection({ buttonsWithNotifyClick, canEditPassword, conference, locked, password, passwordEditEnabled, passwordNumberOfDigits, setPassword, setPasswordEditEnabled, t }) {
    const formRef = useRef(null);
    const [passwordVisible, setPasswordVisible] = useState(false);
    /**
     * Callback invoked to set a password on the current JitsiConference.
     *
     * @param {string} enteredPassword - The new password to be used to lock the
     * current JitsiConference.
     * @private
     * @returns {void}
     */
    function onPasswordSubmit(enteredPassword) {
        if (enteredPassword && passwordNumberOfDigits && !DIGITS_ONLY.test(enteredPassword)) {
            // Don't set the password.
            return;
        }
        setPassword(conference, conference.lock, enteredPassword);
    }
    /**
     * Toggles whether or not the password should currently be shown as being
     * edited locally.
     *
     * @private
     * @returns {void}
     */
    function onTogglePasswordEditState() {
        if (typeof APP === 'undefined' || !buttonsWithNotifyClick?.length) {
            setPasswordEditEnabled(!passwordEditEnabled);
            return;
        }
        let notifyMode;
        const notify = buttonsWithNotifyClick.find((btn) => (typeof btn === 'string' && btn === KEY)
            || (typeof btn === 'object' && btn.key === KEY));
        if (notify) {
            notifyMode = typeof notify === 'string' || notify.preventExecution
                ? NOTIFY_CLICK_MODE.PREVENT_AND_NOTIFY
                : NOTIFY_CLICK_MODE.ONLY_NOTIFY;
            APP.API.notifyToolbarButtonClicked(KEY, notifyMode === NOTIFY_CLICK_MODE.PREVENT_AND_NOTIFY);
        }
        if (notifyMode === NOTIFY_CLICK_MODE.ONLY_NOTIFY) {
            setPasswordEditEnabled(!passwordEditEnabled);
        }
    }
    /**
     * Method to remotely submit the password from outside of the password form.
     *
     * @returns {void}
     */
    function onPasswordSave() {
        if (formRef.current) {
            // @ts-ignore
            const { value } = formRef.current.querySelector('div > input');
            if (value) {
                onPasswordSubmit(value);
            }
        }
    }
    /**
     * Callback invoked to unlock the current JitsiConference.
     *
     * @returns {void}
     */
    function onPasswordRemove() {
        onPasswordSubmit('');
    }
    /**
     * Copies the password to the clipboard.
     *
     * @returns {void}
     */
    function onPasswordCopy() {
        copyText(password ?? '');
    }
    /**
     * Callback invoked to show the current password.
     *
     * @returns {void}
     */
    function onPasswordShow() {
        setPasswordVisible(true);
    }
    /**
     * Callback invoked to hide the current password.
     *
     * @returns {void}
     */
    function onPasswordHide() {
        setPasswordVisible(false);
    }
    function renderPasswordActions() {
        if (!canEditPassword) {
            return null;
        }
        if (passwordEditEnabled) {
            return ((React.createElement("div", { className: classes.formButtonsContainer },
                React.createElement(Button, { accessibilityLabel: t("dialog.Cancel"), labelKey: "dialog.Cancel", onClick: onTogglePasswordEditState, className: classes.cancelButton, type: "tertiary" }),
                React.createElement(Button, { accessibilityLabel: t("dialog.add"), id: "security-password-add-button", labelKey: "dialog.add", onClick: onPasswordSave }))));
        }
        if (locked) {
            return ((React.createElement("div", { className: classes.controlButtonsContainer },
                React.createElement(Button, { type: BUTTON_TYPES.DESTRUCTIVE, color: "warning", accessibilityLabel: t("dialog.Remove"), id: "security-password-remove-button", onClick: onPasswordRemove, labelKey: "dialog.Remove" }),
                React.createElement("div", { className: classes.formButtonsContainer },
                    locked === LOCKED_LOCALLY && (React.createElement(Button, { accessibilityLabel: t(passwordVisible ? "dialog.hide" : "dialog.show"), id: "security-password-display-button", onClick: passwordVisible ? onPasswordHide : onPasswordShow, labelKey: passwordVisible ? "dialog.hide" : "dialog.show", type: BUTTON_TYPES.TERTIARY })),
                    password ? (React.createElement(React.Fragment, null,
                        React.createElement(CopyButton, { accessibilityText: t("dialog.copy"), className: "invite-more-dialog-conference-url", displayedText: t("dialog.copy"), id: "security-password-copy-button", textOnCopySuccess: t("dialog.copied"), textOnHover: t("dialog.copy"), textToCopy: password }))) : null))));
        }
        return ((React.createElement("button", { className: "add-password as-link", onClick: onTogglePasswordEditState, type: "button" }, t("info.addPassword"))));
    }
    const { classes } = useStyles();
    return ((React.createElement("div", { className: "security-dialog password-section" },
        React.createElement("p", { className: "description" }, t(canEditPassword ? "security.about" : "security.aboutReadOnly")),
        React.createElement("div", { className: "security-dialog password", ref: formRef },
            React.createElement(PasswordForm, { editEnabled: passwordEditEnabled, locked: locked, onSubmit: onPasswordSubmit, password: password, passwordNumberOfDigits: passwordNumberOfDigits, visible: passwordVisible }),
            React.createElement("div", { className: classes.container }, renderPasswordActions())))));
}
export default translate(PasswordSection);
